import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Save, Loader2, KeyRound } from 'lucide-react';
import { ColumnMeta } from '../../Types/DataServiceType';

interface Props {
  isOpen: boolean;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  tableName: string;
  alias: string;
  isProduction: boolean;
  primaryKeys: string[];
  columns: ColumnMeta[];
  row: Record<string, unknown> | null;
  onCancel: () => void;
  onConfirm: (request: {
    key: Record<string, unknown>;
    values: Record<string, unknown>;
    confirmProductionAlias?: string;
  }) => Promise<void>;
}

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const stringify = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const RowEditorModal = ({
  isOpen,
  operation,
  tableName,
  alias,
  isProduction,
  primaryKeys,
  columns,
  row,
  onCancel,
  onConfirm,
}: Props) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [keyValues, setKeyValues] = useState<Record<string, string>>({});
  const [typedAlias, setTypedAlias] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // An insert offers the writable columns: no generated identity, and no blob
  // or raw-byte column, neither of which has a sensible text representation.
  const insertColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          !column.autoIncrement &&
          !column.primaryKey &&
          column.controlKind !== 'BINARY' &&
          column.controlKind !== 'UUID'
      ),
    [columns]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setTypedAlias('');
    setBusy(false);

    if (operation === 'INSERT') {
      setValues({});
      setKeyValues({});
      return;
    }

    const nextValues: Record<string, string> = {};
    const nextKeys: Record<string, string> = {};
    Object.keys(row ?? {}).forEach((column) => {
      const raw = (row as Record<string, unknown>)[column];
      if (primaryKeys.includes(column)) {
        nextKeys[column] = stringify(raw);
      } else if (operation === 'UPDATE') {
        nextValues[column] = stringify(raw);
      }
    });
    setValues(nextValues);
    setKeyValues(nextKeys);
  }, [isOpen, operation, row, primaryKeys]);

  const handleConfirm = async () => {
    setError('');

    if (operation !== 'INSERT' && primaryKeys.length === 0) {
      setError('This table has no primary key, so rows cannot be changed safely.');
      return;
    }

    if (isProduction && typedAlias !== alias) {
      setError(`Type the alias exactly: ${alias}`);
      return;
    }

    // Insert only sends the fields that were actually filled in, so every other
    // column keeps its database default. Update only sends what changed.
    let payloadValues: Record<string, string>;
    if (operation === 'INSERT') {
      payloadValues = {};
      insertColumns.forEach((column) => {
        const value = values[column.columnName];
        if (value !== undefined && value !== '') {
          payloadValues[column.columnName] = value;
        }
      });
      if (Object.keys(payloadValues).length === 0) {
        setError('Enter at least one field to insert.');
        return;
      }
    } else {
      payloadValues = {};
      Object.keys(values).forEach((column) => {
        if (values[column] !== stringify((row ?? {})[column])) {
          payloadValues[column] = values[column];
        }
      });
    }

    setBusy(true);
    try {
      await onConfirm({
        key: primaryKeys.length > 0 ? keyValues : {},
        values: operation === 'DELETE' ? {} : payloadValues,
        ...(isProduction ? { confirmProductionAlias: typedAlias } : {}),
      });
    } catch (err) {
      setError('The change could not be applied.');
    } finally {
      setBusy(false);
    }
  };

  const target = operation === 'INSERT' ? 'New row' : `Edit row in ${tableName}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !busy && onCancel()}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl border ${
                    operation === 'DELETE'
                      ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                      : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                  }`}
                >
                  <Save size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">{target}</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {operation === 'INSERT'
                      ? 'Add a row to the selected table'
                      : operation === 'UPDATE'
                      ? 'Only the fields you change are written'
                      : 'The whole row is removed by primary key'}
                  </p>
                </div>
              </div>
              <button
                onClick={onCancel}
                disabled={busy}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              {error && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {error}
                </div>
              )}

              {operation !== 'INSERT' && (
                <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    <KeyRound size={12} /> Primary key
                  </p>
                  <div className="space-y-2">
                    {primaryKeys.map((column) => (
                      <div key={column}>
                        <label className={labelClass}>{column}</label>
                        <input
                          type="text"
                          value={keyValues[column] ?? ''}
                          onChange={(e) => setKeyValues((prev) => ({ ...prev, [column]: e.target.value }))}
                          className={fieldClass}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {operation === 'INSERT' && (
                <div>
                  <p className="mb-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Fields
                  </p>
                  {insertColumns.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-slate-700 py-5 text-center text-[11px] font-bold text-slate-500">
                      This table has no editable columns, so a row can only be added with
                      database defaults.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {insertColumns.map((column) => (
                          <div key={column.columnName}>
                            <label className={labelClass}>
                              {column.columnName}
                              {column.nullable ? '' : ' *'}
                            </label>
                            <input
                              type="text"
                              value={values[column.columnName] ?? ''}
                              onChange={(e) =>
                                setValues((prev) => ({ ...prev, [column.columnName]: e.target.value }))
                              }
                              placeholder={column.columnDefault ?? 'database default'}
                              className={fieldClass}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-[10px] font-bold text-slate-500">
                        Leave a field empty to let the database default apply.
                      </p>
                    </>
                  )}
                </div>
              )}

              {operation === 'UPDATE' && Object.keys(values).length > 0 && (
                <div>
                  <p className="mb-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Fields
                  </p>
                  <div className="space-y-2">
                    {Object.keys(values).map((column) => (
                      <div key={column}>
                        <label className={labelClass}>{column}</label>
                        <input
                          type="text"
                          value={values[column] ?? ''}
                          onChange={(e) => setValues((prev) => ({ ...prev, [column]: e.target.value }))}
                          className={fieldClass}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isProduction && (
                <div>
                  <label className={labelClass}>
                    Type <span className="text-cyan-400">{alias}</span> to confirm this production
                    change
                  </label>
                  <input
                    type="text"
                    value={typedAlias}
                    onChange={(e) => setTypedAlias(e.target.value)}
                    placeholder={alias}
                    className={fieldClass}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy}
                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition disabled:opacity-50 ${
                  operation === 'DELETE' ? 'bg-rose-500 hover:bg-rose-400' : 'bg-cyan-500 text-slate-950 hover:bg-cyan-400'
                }`}
              >
                {busy && <Loader2 size={15} className="animate-spin" />}
                {busy ? 'Applying' : operation === 'DELETE' ? 'Delete row' : operation === 'INSERT' ? 'Insert row' : 'Save row'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RowEditorModal;
